<script setup lang="ts">
// components
import CiFormsUploadPictures from '@/components/upload/CiFormsUploadPictures.vue';
import CiFormsUploadVideos from '@/components/upload/CiFormsUploadVideos.vue';
import CiSkeletonUpload from '@/components/skeleton/CiSkeletonUpload.vue';

/********************
 * COMPOSITIONS      *
 ********************/
const campsiteStore = useCampsiteStore();
const getRouteBaseName = useRouteBaseName();
const localePath = useLocalePath();
const route = useRoute();
const seoStore = useSeoStore();
const userService = useUserService();
const userStore = useUserStore();
const { $gettext } = useGettext();
const { fetchError, fetchFailed, fetchStatus } = useFetchError();
const { i18nTermsOfUseLink, i18nPoliciesLink } = useI18nMeta();
const { locale } = useI18n();
const { openModal, closeModal } = useCiModal();
const { openModalUserProfileIncomplete, openModalError } = useModalDialog({ campsite: () => campsiteStore.campsite });

/********************
 * INITIALIZATION    *
 ********************/
const { pending, error } = await useLazyAsyncData(`upload-${route.params.identifier}`, async () => {
  const campsite = campsiteStore.campsite;

  try {
    if (!campsite || campsite.slug !== route.params.identifier) {
      const campsite = await campsiteStore.getCampsite({ slug: route.params.identifier, lang: locale.value });
      seoStore.setDynamicMetaParam({ campsite_name: campsite.meta.name });
      return campsite;
    }
    seoStore.setDynamicMetaParam({ campsite_name: campsite.meta.name });
    return campsite;
  } catch (error) {
    fetchError(error);
  }
});

if (error.value) {
  throw createError({ statusCode: error.value?.statusCode, message: error.value.statusMessage });
}

/********************
 * REFS & VARS       *
 ********************/
const selectedTabIndex = computed(() => {
  return getRouteBaseName(route) === RBN_IMAGE_UPLOAD ? 0 : 1;
});

const currentTabComponent = computed(() => {
  if (selectedTabIndex.value === 0) {
    return CiFormsUploadPictures;
  }
  return CiFormsUploadVideos;
});

watch(() => userStore.user.profile, onUserChange, { immediate: true });

function onUserChange(newValue) {
  if (newValue) {
    checkPermissions();
  }
}

function checkPermissions() {
  if (!userStore.user.profile) {
    return;
  }

  userService
    .canCreateCampsiteMedia(locale.value)
    .then((response) => {
      if (response?.reason) {
        const { code } = response.reason;
        if (code !== 'userprofile_incomplete') {
          missingPermission(response);
        } else if (response.reason.code === 'userprofile_incomplete') {
          incompleteProfile();
        }
      }
    })
    .catch(() => {});
}

function missingPermission(perm) {
  const buttons = [
    {
      label: $gettext('Back to campsite'),
      to: localePath({
        name: RBN_CAMPSITE_DETAIL,
        params: { identifier: campsiteStore.campsite?.slug },
      }),
      classes: ['button--warning'],
    },
  ];
  openModalError(buttons, $gettext('Permission error'), perm.reason.message);
}

function incompleteProfile() {
  const fields = [];

  if (!userStore.user.profile?.first_name) {
    fields.push({
      label: $gettext('firstname'),
      name: 'first_name',
      placeholder: $gettext('Enter firstname'),
      required: true,
      type: 'text',
    });
  }

  if (!userStore.user.profile?.last_name) {
    fields.push({
      label: $gettext('lastname'),
      name: 'last_name',
      placeholder: $gettext('Enter lastname'),
      required: true,
      type: 'text',
    });
  }

  openModalUserProfileIncomplete(fields, $gettext('Please complete your user data to upload images of videos:'), true);
}

function onClickGuidelines(evt) {
  if (evt.target.hash === '#policies') {
    evt.preventDefault();

    const CiModalUploadGuidelines = defineAsyncComponent({
      loader: () => import('@/components/CiModal/CiModalUploadGuidelines.vue'),
    });

    openModal(
      {
        component: CiModalUploadGuidelines,
        attrs: {
          name: 'CiModalUploadGuidelines',
          closeable: true,
          title: $gettext('Guidelines for uploading photos and videos'),
          onClose: () => {
            closeModal('CiModalUploadGuidelines');
          },
        },
      },
      {
        clickToClose: true,
        contentTransition: 'translate-y-down',
        contentClass: 'max-w-[1140px] w-full h-auto m-4',
      },
    );
  }
}
</script>

<template>
  <div>
    <client-only>
      <template v-if="!fetchFailed && !pending">
        <div class="campsite-upload">
          <div class="bg-gray-10">
            <div class="container py-2">
              <CiNavigationToggle
                type="black"
                :href="localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: route.params.identifier } })"
              >
                <span class="ml-4 text-dark">{{ $gettext('Back to campsite') }}</span>
              </CiNavigationToggle>
            </div>
          </div>
          <section class="upload-form-wrapper my-4">
            <div class="container mb-6">
              <div class="row">
                <div class="col-12">
                  <h1 class="mt-4 text-center lg:text-left">
                    {{
                      $gettext(
                        'Upload images or Videos for %{ campsite }',
                        {
                          campsite: campsiteStore.campsite?.meta ? campsiteStore.campsite.meta.name : '...',
                        },
                        true,
                      )
                    }}
                  </h1>
                  <p class="mb-12">
                    <span
                      @click="onClickGuidelines"
                      v-html="
                        $gettext(
                          'Upload your pictures or videos from the campsite and add a description. Our %{termsLink} and %{policiesLink} apply.',
                          { termsLink: i18nTermsOfUseLink, policiesLink: i18nPoliciesLink },
                          true,
                        )
                      "
                    />
                  </p>
                </div>
                <div class="col-12">
                  <ul
                    class="tabs m-0 flex list-none flex-col divide-y divide-gray-30 border-gray-30 p-0 md:flex-row md:justify-center md:divide-y-0 md:border-b"
                  >
                    <li class="">
                      <nuxt-link
                        v-if="route"
                        :to="localePath({ name: RBN_IMAGE_UPLOAD, params: route.params, query: route.query })"
                        class="-mb-px block border border-transparent px-4 py-2 text-dark hover:no-underline"
                      >
                        <span>{{ $gettext('Upload Images') }}</span>
                      </nuxt-link>
                    </li>
                    <li class="">
                      <nuxt-link
                        v-if="route"
                        :to="localePath({ name: RBN_VIDEO_UPLOAD, params: route.params, query: route.query })"
                        class="-mb-px block border border-transparent px-4 py-2 text-dark hover:no-underline"
                      >
                        <span>{{ $gettext('Upload Videos') }}</span>
                      </nuxt-link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <component
              :is="currentTabComponent"
              v-if="campsiteStore.campsite"
              :campsite="campsiteStore.campsite"
            />
          </section>
        </div>
      </template>
      <CiErrorView
        v-else
        :error="fetchStatus"
      />
      <template #placeholder>
        <CiSkeletonUpload />
      </template>
    </client-only>
  </div>
</template>

<style lang="scss" scoped>
.tabs .router-link-exact-active {
  @apply border-white bg-white text-primary md:rounded-tl md:rounded-tr md:border-gray-30  md:border-b-white;
}
</style>
